import { useMutation } from '@tanstack/react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { callService } from '@api/remote-call';
import {
  LetUsCallYouFormData,
  LetUsCallYouProps,
} from '@components/ui/LetUsCallYou/types';
import { ApiError, CallMeFormRequestViewModel } from '@api/v3';

const useCallMeFrom = ({
  entityId,
  entityName,
  price,
  currency,
  salesItemType,
  availabilityStatus,
  formCheckInDate,
  formCheckOutDate,
  hotelRoomList,
  onClose,
  onFormSendSuccess,
  channel,
  setErrorMsg,
}: LetUsCallYouProps) => {
  const {
    mutate,
    isLoading: isPostCallMeLoading,
    error,
    isSuccess: isPostCallMeSuccess,
  } = useMutation(
    ['letUsCallYou'],
    (body: CallMeFormRequestViewModel) =>
      callService(
        { service: 'TicketsService', method: 'callMeForm', version: 'v3' },
        { body }
      ),
    {
      onSuccess: () => {
        onClose();
        if (onFormSendSuccess) {
          onFormSendSuccess();
        }
      },
      onError: (error: ApiError) => {
        setErrorMsg(error.body.errors[0].detail);
      },
    }
  );

  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmit = async ({
    firstName,
    lastName,
    phone,
    contactOption,
    availableTime,
    message,
  }: LetUsCallYouFormData) => {
    if (!executeRecaptcha) return;
    const token = await executeRecaptcha('letUsCallYou');

    mutate({
      firstName,
      lastName,
      phone,
      contactOption,
      message,
      entityId,
      entityName,
      price,
      currency,
      token,
      formCheckInDate: formCheckInDate?.format('YYYY-MM-DDTHH:mm:ss'),
      formCheckOutDate: formCheckOutDate?.format('YYYY-MM-DDTHH:mm:ss'),
      roomList: hotelRoomList?.map((hotelRoom) => ({
        adultCount: hotelRoom.adultCount,
        childList: hotelRoom.childrenList?.map(({ age }) => ({
          childBirthDate: String(age).split('/').reverse().join('-'),
        })),
      })),
      availabilityStatus,
      availableTime,
      salesItemType,
      channel,
    });
  };

  return {
    onSubmit,
    isPostCallMeLoading,
    error,
    isPostCallMeSuccess,
  };
};

export default useCallMeFrom;
